import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { PubOwnerDomain, PubOwnerRole } from "../../api/cafe";
import {
  acceptTournamentTicketBuyRequest,
  getTournamentTicketBuyRequests,
  rejectTournamentTicketBuyRequest,
  TournamentTicketBuyRequestItem,
} from "../../api/tournament-ticket";
import { pubState } from "../../recoil/pub.atom";
import * as Store from "../tournament-ticket-store/styles";
import * as S from "./styles";

const formatRequestedAt = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const TournamentTicketRequest = () => {
  const pub = useRecoilValue(pubState);
  const navigate = useNavigate();
  const [list, setList] = useState<TournamentTicketBuyRequestItem[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!pub) return;
    const res = await getTournamentTicketBuyRequests(pub.id);
    setList(res.list ?? []);
  }, [pub]);

  useEffect(() => {
    if (!pub) {
      navigate("/");
      return;
    }
    if (pub.role === PubOwnerRole.OWNER || pub.domains.length === 0) return;
    if (!pub.domains.includes(PubOwnerDomain.TICKET)) {
      navigate("/");
      alert("해당 페이지 접근 권한이 없습니다. ");
    }
  }, [navigate, pub]);

  useEffect(() => {
    load();
  }, [load]);

  const accept = async (item: TournamentTicketBuyRequestItem) => {
    if (!pub || busyId != null) return;
    if (
      !window.confirm(
        `${item.nickname}님의 ${item.ticketName} ${item.count}장 요청을 수락할까요?`,
      )
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      const res = await acceptTournamentTicketBuyRequest(pub.id, item.id);
      if (res.code !== 200) {
        alert(res.message || "수락에 실패했습니다.");
        return;
      }
      await load();
    } catch (e: any) {
      alert(e?.message || "수락에 실패했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (item: TournamentTicketBuyRequestItem) => {
    if (!pub || busyId != null) return;
    if (
      !window.confirm(
        `${item.nickname}님의 ${item.ticketName} ${item.count}장 요청을 거절할까요?`,
      )
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      const res = await rejectTournamentTicketBuyRequest(pub.id, item.id);
      if (res.code !== 200) {
        alert(res.message || "거절에 실패했습니다.");
        return;
      }
      await load();
    } catch (e: any) {
      alert(e?.message || "거절에 실패했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Store.TournamentTicketStoreWrapper>
      <Store.Title>교환 요청</Store.Title>
      <Store.HeaderWrapper>
        <div className="box">
          <div className="label">대기 요청:</div>
          <div className="value">{list.length.toLocaleString()}건</div>
        </div>
      </Store.HeaderWrapper>
      <S.Panel>
        <S.PanelTitle>대기 목록</S.PanelTitle>
        <S.Divider />
        <S.Table>
          <S.Head>
            <div className="item time">요청 시각</div>
            <div className="item nickname">닉네임</div>
            <div className="item ticket">티켓</div>
            <div className="item count">수량</div>
            <div className="item action" />
          </S.Head>
          <S.List>
            {list.length === 0 ? (
              <S.Empty>대기 중인 교환 요청이 없습니다.</S.Empty>
            ) : (
              list.map((item) => (
                <S.Row key={item.id}>
                  <div className="item time">
                    {formatRequestedAt(item.createdAt)}
                  </div>
                  <div className="item nickname">{item.nickname}</div>
                  <div className="item ticket">{item.ticketName}</div>
                  <div className="item count">
                    {item.count.toLocaleString()}장
                  </div>
                  <div className="item action">
                    <S.Actions>
                      <S.AcceptButton
                        type="button"
                        disabled={busyId != null}
                        onClick={() => accept(item)}
                      >
                        수락
                      </S.AcceptButton>
                      <S.RejectButton
                        type="button"
                        disabled={busyId != null}
                        onClick={() => reject(item)}
                      >
                        거절
                      </S.RejectButton>
                    </S.Actions>
                  </div>
                </S.Row>
              ))
            )}
          </S.List>
        </S.Table>
      </S.Panel>
    </Store.TournamentTicketStoreWrapper>
  );
};

export default TournamentTicketRequest;
